import { Check } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface OnboardingChoiceCardProps {
  /** Icon for the top-left ico-tile. Pass a Lucide icon component. */
  icon: ReactNode;
  /** Card title — bold, wraps. Use <span className="ital">…</span> for the italic accent. */
  name: ReactNode;
  /** Body description. */
  desc: ReactNode;
  /** Mono uppercase meta line below the description (e.g. "→ Lo más común"). Optional. */
  meta?: ReactNode;
  /** When true, the card paints in the selected state (olive border + accent-100 bg + olive icon tile). */
  selected?: boolean;
  /** When true, card is non-interactive and dimmed. */
  disabled?: boolean;
  /** Optional badge in the top-right corner (e.g. "Próximamente"). Hides the check-dot. */
  badge?: ReactNode;
  /** Optional ribbon in the top-left corner (e.g. "Recomendado"). */
  ribbon?: ReactNode;
  /** Click handler. Ignored when disabled. */
  onClick?: () => void;
  /** Pass aria-label when name is short or icon-only. */
  ariaLabel?: string;
}

export function OnboardingChoiceCard({
  icon,
  name,
  desc,
  meta,
  selected = false,
  disabled = false,
  badge,
  ribbon,
  onClick,
  ariaLabel,
}: OnboardingChoiceCardProps): JSX.Element {
  return (
    <button
      type="button"
      className={cn(
        "fx-onb-choice",
        selected && "selected",
        disabled && "disabled",
      )}
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={ariaLabel}
    >
      {ribbon ? <span className="ribbon">{ribbon}</span> : null}
      {badge ? (
        <span className="badge">{badge}</span>
      ) : (
        <span className="check-dot" aria-hidden>
          <Check />
        </span>
      )}
      <span className="ico-tile">{icon}</span>
      <span className="name">{name}</span>
      <span className="desc">{desc}</span>
      {meta ? <span className="meta">{meta}</span> : null}
    </button>
  );
}
